import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Platform } from 'react-native';

import BasicLayout from '../../../../common/components/CustomLayout/BasicLayout';
import { TextSmallSize, TextMediumSize, TextdefaultSize } from '../../../../common/constants/Fonts';
import { msgStr } from '../../../../common/constants/Message';
import { useAlertModal } from '../../../../common/hooks';
import { BOHToolbar, renderBOHTlbDatePicker } from '../../../../common/components/bohtoolbar';
import { CommonContainer } from '../../../../common/components/CustomLayout';
import { BOHTBody, BOHTD, BOHTDIconBox, BOHTH, BOHTHead, BOHTR, BOHTable  } from '../../../../common/components/bohtable';
import { getProductReservations } from '../../../../api/Product';
import { navigateBackToInventory } from '../../inventoryNavigationState';

interface ReservationItem {
  reservation_item_id: number;
  reservation_id: number;
  quantity: number;
  price: number;
  status: string | null;
  reservation: {
    id: number;
    order_number: string;
    start_date: string;
    end_date: string;
    start_time: string;
    end_time: string;
    stage: number;
    total_price: number;
    customer?: {
      id: number;
      full_name: string;
      email: string;
      phone_number: string;
    };
    brand?: {
      id: number;
      brand: string | null;
    };
  };
}

const ProductHistory = ({ navigation, route }) => {
  const { showAlert } = useAlertModal();
  const product = route?.params?.product;

  // const now = new Date();
  // const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  // const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  // const [ searchOptions, setSearchOptions ] = useState({
  //   start_date : `${firstDayOfMonth.getFullYear()}-${String(firstDayOfMonth.getMonth() + 1).padStart(2, '0')}-${String(firstDayOfMonth.getDate()).padStart(2, '0')}`,
  //   end_date : `${lastDayOfMonth.getFullYear()}-${String(lastDayOfMonth.getMonth() + 1).padStart(2, '0')}-${String(lastDayOfMonth.getDate()).padStart(2, '0')}`,
  // });
  const [reservations, setReservations] = useState<ReservationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // const changeSearchOptions = (key: string, val: string) => {
  //   setSearchOptions(prevOptions => ({
  //     ...prevOptions,
  //     [key]: val
  //   }));
  // }

  const productLabel = useMemo(() => {
    if (!product) return '';
    const line = product?.line?.line ? `${product.line.line}` : '';
    const size = product?.line?.size ? ` (${product.line.size})` : '';
    const barcode = product?.barcode ? ` - ${product.barcode}` : '';
    return `${line}${size}${barcode}` || `${product?.id ?? ''}`;
  }, [product]);

  const fetchReservations = useCallback(async () => {
    if (!product?.id) return;
    setIsLoading(true);
    try {
      const payload = { 
        product_id: product.id
        // start_date: searchOptions.start_date, 
        // end_date: searchOptions.end_date 
      };
      
      getProductReservations(payload, (jsonRes, status, error) => {
        switch (status) {
          case 200:
            setReservations(jsonRes?.reservations ?? []);
            break;
          case 500:
            showAlert('error', msgStr('serverError'));
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
        setIsLoading(false);
      });
    } catch (error) {
      console.log('Failed to load order history', error);
      showAlert('error', msgStr('unknownError'));
      setIsLoading(false);
    }
  }, [product?.id, showAlert]);

  useEffect(() => {
    if (product?.id) {
      fetchReservations();
    }
  }, [product?.id]);

  const [sorting, setSorting] = useState({ key: '', direction: null });

  const headers = [
    { key: "order_number", label: "Order #", width: 100 },
    { key: "customer_name", label: "Customer", width: 150 },
    { key: "brand", label: "Brand", width: 150 },
    { key: "start_date", label: "Start Date", width: 100 },
    { key: "end_date", label: "End Date", width: 100 },
    { key: "quantity", label: "Quantity", width: 80 },
    { key: "stage", label: "Stage", width: 100 },
    { key: "start_time", label: "Start Time", width: 100 },
    { key: "end_time", label: "End Time", width: 100 },
  ];

  const convertStageToString = (stage) => {
    switch (stage) {
      case null: case 'null': 
        return 'Draft';
      case 0: case '0': 
        return 'Draft';
      case 1: case '1': 
        return 'Provisional';
      case 2: case '2': 
        return 'Confirmed';
      case 3: case '3': 
        return 'Checked out';
      case 4: case '4': 
        return 'Checked in';
      case 5: case '5': return 'Canceled';
      default:  return 'Invalid stage';
    }
  };

  const handleSort = (key: string) => {
    setSorting(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderTableData = () => {
    if (!reservations.length) {
      return (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>No reservations found for the selected range.</Text>
        </View>
      );
    }

    return reservations.map((reservation, index) => (
      <BOHTR key={reservation.reservation_item_id}>
        <BOHTD width={headers[0].width}>{reservation.reservation.order_number ?? '-'}</BOHTD>
        <BOHTD width={headers[1].width}>{reservation.reservation.customer?.full_name ?? '-'}</BOHTD>
        <BOHTD width={headers[2].width}>{reservation.reservation.brand?.brand ?? '-'}</BOHTD>
        <BOHTD width={headers[3].width}>{reservation.reservation.start_date ?? '-'}</BOHTD>
        <BOHTD width={headers[4].width}>{reservation.reservation.end_date ?? '-'}</BOHTD>
        <BOHTD width={headers[5].width}>{reservation.quantity ?? '-'}</BOHTD>
        <BOHTD width={headers[6].width}>{convertStageToString(reservation.reservation.stage) ?? '-'}</BOHTD>
        <BOHTD width={headers[7].width}>{reservation.reservation.start_time ?? '-'}</BOHTD>
        <BOHTD width={headers[8].width}>{reservation.reservation.end_time ?? '-'}</BOHTD>
      </BOHTR>
    ));
  };

  const tableElement = useMemo(()=>{
    return(<BOHTable isLoading={isLoading}>
      <BOHTHead>
        <BOHTR>
          {headers.map(({ key, label, width }) => (
            <BOHTH
              key={key}
              width={width}
              isSorted={sorting.key === key}
              direction={sorting.key === key ? sorting.direction : null}
              onClick={() => handleSort(key)}
            >
              {label}
            </BOHTH>
          ))}
        </BOHTR>
      </BOHTHead>
      <BOHTBody>
        {renderTableData()}
      </BOHTBody>
    </BOHTable>)}, [isLoading, reservations, sorting])

  const handleGoBack = () => {
    navigateBackToInventory(navigation, 'Products');
  };

  return (
    <BasicLayout
      navigation={navigation}
      goBack={handleGoBack}
      screenName={'Product History'}
    >
      <CommonContainer>
        <Text style={styles.heading}>Product History</Text>
        {productLabel ? <Text style={styles.productLabel}>Product: {productLabel}</Text> : null}
        {/* <BOHToolbar style={{zIndex:100}}>
          <Text style={{marginRight:8, fontSize:TextdefaultSize}}>Start</Text>
            {Platform.OS == 'web' && 
              renderBOHTlbDatePicker(searchOptions.start_date, (date) => {
                const year = date.getFullYear();
                const formattedDate = `${year}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
                changeSearchOptions('start_date', formattedDate);
            })}
            <Text style={{marginHorizontal:8, fontSize:TextdefaultSize}}>End</Text>
            {Platform.OS == 'web' && 
              renderBOHTlbDatePicker(searchOptions.end_date, (date) => {
                const year = date.getFullYear();
                const formattedDate = `${year}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
                changeSearchOptions('end_date', formattedDate);
            })}
              
        </BOHToolbar> */}
        {tableElement}
      </CommonContainer>
    </BasicLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
  },
  heading: {
    fontSize: TextMediumSize,
    fontWeight: '700',
    marginBottom: 6,
  },
  productLabel: {
    fontSize: TextSmallSize,
    fontWeight: '600',
    marginBottom: 14,
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 12,
  },
  filterField: {
    flex: 1,
    marginRight: 10,
  },
  label: {
    marginBottom: 6,
    fontSize: TextSmallSize,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    fontSize: TextSmallSize,
    backgroundColor: 'white',
  },
  applyButton: {
    backgroundColor: '#2e96e1',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 6,
  },
  applyText: {
    color: 'white',
    fontSize: TextSmallSize,
  },
  tableContainerOverride: {
    marginTop: 8,
  },
  headerCell: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    fontWeight: '600',
    fontSize: TextSmallSize,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  cell: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    fontSize: TextSmallSize,
  },
  emptyState: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: TextSmallSize,
    color: '#666',
  },
});

export default ProductHistory;
